import React, { useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "reactstrap";

import SectionCard from "components/SectionCard";
import CoberturaEditor, { validarCobertura, coberturaParaPayload, coberturaDaApi, rotaVazia } from "components/CoberturaEditor";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useToast } from "hooks/toast";
import { redeCobertura, salvarRedeCobertura, redeEstados, redeCidades, mensagemErro } from "services/meuFreteApi";

// Aba "Cobertura" do Cadastro do MeuFrete (PO1-3648, épico PO1-3359): a
// transportadora da Rede Everlog revisa as rotas origem → destino que
// declarou no pedido. Salvar substitui a lista inteira (PUT) e o backend
// atualiza o Sourcing na hora. Mesmo editor da página pública, com as funções
// de carga autenticadas.

const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" };
const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };

export default function AbaCobertura() {
  const { addToast } = useToast();
  const [rotas, setRotas] = useState([rotaVazia()]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [erroLista, setErroLista] = useState("");
  const [sujo, setSujo] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErroLista("");
    try {
      const items = await redeCobertura();
      setRotas(items.length ? coberturaDaApi(items) : [rotaVazia()]);
      setSujo(false);
    } catch (err) {
      setErroLista(mensagemErro(err, "Não foi possível carregar a cobertura."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const salvar = async () => {
    const e = validarCobertura(rotas);
    setErro(e);
    if (e) return;
    setSalvando(true);
    try {
      const items = await salvarRedeCobertura(coberturaParaPayload(rotas));
      setRotas(items.length ? coberturaDaApi(items) : rotas);
      setSujo(false);
      addToast({ place: "tr", type: "success", title: "Cobertura salva", description: `${items.length || coberturaParaPayload(rotas).length} rota(s). O Sourcing já considera a cobertura nova.` });
    } catch (err) {
      const d = (err && err.response && err.response.data) || {};
      if (d.field === "rotas") setErro(d.error || "Confira as rotas.");
      addToast({ place: "tr", type: "danger", title: "Não foi possível salvar a cobertura", description: mensagemErro(err) });
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <div style={stateBoxStyle}>
        <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
        <span>Carregando a cobertura…</span>
      </div>
    );
  }
  if (erroLista) {
    return (
      <div style={stateBoxStyle} role="alert">
        {erroLista}{" "}
        <Button size="sm" color="info" outline onClick={carregar}>
          Tentar de novo
        </Button>
      </div>
    );
  }

  return (
    <SectionCard
      eyebrow="Rede Everlog"
      title="Cobertura declarada"
      description="Onde a sua transportadora atende: cada rota é uma origem e um destino, por UF inteira ou por cidade. É isso que faz você aparecer no Sourcing, no Leilão e no BID dos embarcadores. Sem preço: a tabela fica na aba ao lado."
      action={
        <Button size="sm" color="info" onClick={salvar} disabled={salvando || !sujo}>
          {salvando ? <Spinner size="sm" /> : "Salvar cobertura"}
        </Button>
      }
    >
      <CoberturaEditor
        value={rotas}
        onChange={(l) => {
          setRotas(l);
          setSujo(true);
          setErro(null);
        }}
        carregarEstados={redeEstados}
        carregarCidades={redeCidades}
        erro={erro}
        disabled={salvando}
      />
      {sujo ? <div style={{ ...mutedStyle, marginTop: "0.5rem" }}>Alterações ainda não salvas.</div> : null}
    </SectionCard>
  );
}
